import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { candidateAPI, requirementAPI, submissionAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Spinner, Badge, SearchableSelect, Input } from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import ResumeButton from '../components/ResumeButton';
import SafeHtml from '../components/SafeHtml';
import { htmlToText, formatChangeValue } from '../utils/html';
import { prettyFieldLabel } from '../utils/labels';
import { fmtLPA, fmtTimeRange } from '../utils/format';

const onlyDigits  = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const onlyDecimal = (v) => String(v ?? '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

const STATUS_VARIANT = {
  Applied:     'default',
  Shortlisted: 'warning',
  Interview:   'info',
  Rejected:    'danger',
  Hired:       'success',
};

const ACTION_ICONS = {
  CREATE_CANDIDATE: '🆕',
  UPDATE_CANDIDATE: '✏️',
  UPDATE_STATUS:    '🔄',
  DELETE_CANDIDATE: '🗑️',
  UPLOAD_RESUME:    '📎',
};

// ── Experience helpers ─────────────────────────────────────────────────────
const splitExp = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n < 0) return { yrs: '', mos: '' };
  const yrs = Math.floor(n);
  const mos = Math.round((n - yrs) * 12);
  return { yrs: yrs === 0 && mos > 0 ? '' : String(yrs), mos: mos > 0 ? String(mos) : '' };
};
const combineExp = (yrs, mos) => {
  const y = parseFloat(yrs) || 0;
  const m = parseFloat(mos) || 0;
  const total = y + m / 12;
  return total > 0 ? parseFloat(total.toFixed(4)) : '';
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function Section({ title, icon, children, isDark }) {
  return (
    <div style={{
      background: isDark ? 'var(--bg-card)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1'}`,
      borderRadius: 14, overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: isDark ? 'rgba(255,255,255,0.02)' : '#fafbfc',
      }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em', wordBreak: 'break-word', minWidth: 0, overflowWrap: 'anywhere' }}>
          {title}
        </h2>
      </div>
      <div style={{ padding: '18px 20px' }}>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value, isDark }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 100, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
}

export default function CandidateProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { isDeveloper } = useAuth();

  // Where should the back button go? Callers pass { state: { from, backLabel } }
  const backTo    = location.state?.from      || '/candidate-finder';
  const backLabel = location.state?.backLabel || 'Back to Finder';
  const isDark = theme === 'dark';

  const [candidate, setCandidate]   = useState(null);
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('overview');

  // ── Resubmit ─────────────────────────────────────────────────────────────
  const [showResubmit,    setShowResubmit]    = useState(false);
  const [allReqs,         setAllReqs]         = useState([]);
  const [resubmitEditForm, setResubmitEditForm] = useState({});
  const [resubmitReqId,   setResubmitReqId]   = useState('');
  const [resubmitNotes,   setResubmitNotes]   = useState('');
  const [resubmitting,    setResubmitting]    = useState(false);

  // ── Edit candidate ────────────────────────────────────────────────────────
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving,   setSaving]   = useState(false);

  const openEdit = () => {
    const c = candidate;
    setEditForm({
      fullName:            c.fullName || '',
      email:               c.email || '',
      phone:               c.phone || '',
      currentCompany:      c.currentCompany || '',
      currentRole:         c.currentRole || '',
      location:            c.location || '',
      preferredLocation:   c.preferredLocation || '',
      experience:          c.experience ?? '',
      experienceYrs:       splitExp(c.experience).yrs,
      experienceMos:       splitExp(c.experience).mos,
      expectedSalary:      c.expectedSalary || '',
      expectedSalaryCurrency: c.expectedSalaryCurrency || 'INR',
      currentSalary:        c.currentSalary  || '',
      currentSalaryCurrency: c.currentSalaryCurrency  || 'INR',
      servingNoticePeriod: c.servingNoticePeriod || false,
      availabilityToInterview: c.availabilityToInterview || '',
      experienceSummary:   c.experienceSummary || '',
      noticePeriodNotes:   c.noticePeriodNotes || '',
      offerInHand:         c.offerInHand || false,
      offerCompany:        c.offerCompany || '',
      offerPackage:        c.offerPackage || '',
      offerDoj:            c.offerDoj ? c.offerDoj.slice(0, 10) : '',
      skills:              Array.isArray(c.skills) ? c.skills.join(', ') : (c.skills || ''),
      notes:               c.notes || '',
      // No clientNotes: client notes live on each Submission now, and the
      // backend drops a candidate-level clientNotes on update.
    });
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!editForm.fullName?.trim()) return toast.error('Full name is required.');
    setSaving(true);
    try {
      await candidateAPI.update(candidate.id, {
        ...editForm,
        experience: combineExp(editForm.experienceYrs, editForm.experienceMos),
        skills: editForm.skills
          ? editForm.skills.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
          : [],
      });
      toast.success('Candidate updated ✓');
      setShowEdit(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await candidateAPI.getFullProfile(id);
      setCandidate(data.candidate);
      setLogs(data.activityLogs || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load candidate profile.');
      navigate('/candidate-finder');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    requirementAPI.dropdown()
      .then(({ data }) => setAllReqs(data.requirements || []))
      .catch(() => {});
  }, []);

  // Must be before any early returns so hook count is always the same
  useLayoutEffect(() => {
    const open = showEdit || showResubmit;
    if (open) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [showEdit, showResubmit]);

  const openResubmit = () => {
    const cand = candidate;
    setResubmitEditForm({
      fullName:            cand.fullName || '',
      email:               cand.email || '',
      phone:               cand.phone || '',
      currentCompany:      cand.currentCompany || '',
      currentRole:         cand.currentRole || '',
      location:            cand.location || '',
      preferredLocation:   cand.preferredLocation || '',
      experience:          cand.experience ?? '',
      experienceYrs:       splitExp(cand.experience).yrs,
      experienceMos:       splitExp(cand.experience).mos,
      expectedSalary:      cand.expectedSalary || '',
      expectedSalaryCurrency: cand.expectedSalaryCurrency || 'INR',
      currentSalary:        cand.currentSalary  || '',
      currentSalaryCurrency: cand.currentSalaryCurrency  || 'INR',
      servingNoticePeriod: cand.servingNoticePeriod || false,
      availabilityToInterview: cand.availabilityToInterview || '',
      experienceSummary:   cand.experienceSummary || '',
      noticePeriodNotes:   cand.noticePeriodNotes || '',
      offerInHand:         cand.offerInHand || false,
      offerCompany:        cand.offerCompany || '',
      offerPackage:        cand.offerPackage || '',
      offerDoj:            cand.offerDoj ? cand.offerDoj.slice(0, 10) : '',
      skills:              Array.isArray(cand.skills) ? cand.skills.join(', ') : (cand.skills || ''),
      notes:               cand.notes || '',
    });
    setResubmitReqId('');
    setResubmitNotes('');
    setShowResubmit(true);
  };

  const handleResubmit = async () => {
    if (!candidate.resumeName) {
      return toast.error(`${candidate.fullName} has no resume on file — upload it first (Candidates → Edit), then resubmit.`);
    }
    if (!resubmitReqId) return toast.error('Please select a requirement.');
    if (!resubmitEditForm.fullName?.trim()) return toast.error('Full name is required.');
    const req = allReqs.find(r => r.id === resubmitReqId);
    if (!req) return;
    setResubmitting(true);
    try {
      // Save updated candidate details first
      await candidateAPI.update(candidate.id, {
        ...resubmitEditForm,
        experience: combineExp(resubmitEditForm.experienceYrs, resubmitEditForm.experienceMos),
        skills: resubmitEditForm.skills
          ? resubmitEditForm.skills.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
          : [],
      });
      // Then create submission
      await submissionAPI.create({
        candidateId:   candidate.id,
        clientId:      req.clientId,
        requirementId: resubmitReqId,
        notes:         resubmitNotes || null,
      });
      toast.success(`Submitted to "${req.jobTitle}" ✓`);
      setShowResubmit(false);
      setResubmitReqId('');
      setResubmitNotes('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally {
      setResubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Spinner /></div>;
  }

  if (!candidate) return null;

  const c = candidate;
  // Submissions are sorted newest-first; first entry is the most recent.
  const latestSub = c.submissions?.[0] || null;
  const interviewSubs = (c.submissions || []).filter(s => s.interviewDate || s.interviewTime || s.interviewMode);
  const hasInterview = interviewSubs.length > 0;

  const tabs = [
    { key: 'overview',   label: 'Overview' },
    { key: 'requirement', label: 'Requirement' },
    { key: 'interview',  label: 'Interview' },
    { key: 'history',    label: `History (${logs.length})` },
  ];

  const tabStyle = (key) => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
    background: activeTab === key
      ? (isDark ? 'rgba(99,102,241,0.2)' : '#6366f1')
      : 'transparent',
    color: activeTab === key
      ? (isDark ? '#c7d2fe' : '#ffffff')
      : 'var(--text-secondary)',
  });

  return (
    <div className="space-y-6">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(backTo)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: isDark ? '#a5b4fc' : '#6366f1', fontSize: 13, fontWeight: 500,
            padding: '4px 0', marginTop: 2,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {backLabel}
        </button>
      </div>

      <div style={{
        background: isDark ? 'var(--bg-card)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1'}`,
        borderRadius: 16, padding: '24px 28px',
        boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
            background: c.isDeleted
              ? (isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0')
              : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: c.isDeleted ? 'var(--text-muted)' : '#fff',
            fontWeight: 700, fontSize: 22,
            boxShadow: c.isDeleted ? 'none' : '0 4px 12px rgba(99,102,241,0.35)',
          }}>
            {c.fullName?.[0]?.toUpperCase()}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>{c.fullName}</h1>
              {latestSub && <Badge variant={STATUS_VARIANT[latestSub.status] || 'default'}>{latestSub.status}</Badge>}
              {c.isDeleted && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: isDark ? '#fca5a5' : '#dc2626', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}` }}>
                  Archived
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: 13, color: 'var(--text-secondary)', wordBreak: 'break-word', minWidth: 0 }}>
              <span style={{ overflowWrap: 'anywhere', wordBreak: 'break-all', minWidth: 0, maxWidth: '100%' }}>✉ {c.email}</span>
              {c.phone && <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>📞 {c.phone}</span>}
              {c.currentRole && <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>💼 {c.currentRole}</span>}
              {c.currentCompany && <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>🏢 {c.currentCompany}</span>}
              {c.location && <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>📍 {c.location}</span>}
            </div>
          </div>

          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Added {formatDate(c.createdAt)}</p>
            {c.createdByUser && (
              <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>by {c.createdByUser.name}</p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
              {/* Was a raw <a> to /candidates/:id/resume. That URL could not carry
                  the JWT, depended on server-held state (so it broke behind two
                  pods), and double-slashed whenever REACT_APP_API_URL ended in
                  "/". ResumeButton fetches a fresh pre-signed S3 URL on click. */}
              {c.resumeName && (
                <ResumeButton
                  candidateId={c.id}
                  resumeName={c.resumeName}
                  style={{
                    fontSize: 12, fontWeight: 500,
                    background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                    color: isDark ? '#a5b4fc' : '#4f46e5',
                    border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
                    padding: '5px 12px', borderRadius: 8,
                  }}
                />
              )}
              {!c.isDeleted && (
                <button
                  onClick={openEdit}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                    color: isDark ? '#a5b4fc' : '#4f46e5',
                    border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
                    padding: '5px 12px', borderRadius: 8,
                  }}
                >
                  ✏️ Edit
                </button>
              )}
              {!c.isDeleted && (
                <button
                  onClick={openResubmit}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: isDark ? 'rgba(20,184,166,0.12)' : '#f0fdfa',
                    color: isDark ? '#5eead4' : '#0f766e',
                    border: `1px solid ${isDark ? 'rgba(20,184,166,0.3)' : '#99f6e4'}`,
                    padding: '5px 12px', borderRadius: 8,
                  }}
                >
                  🔁 Resubmit
                </button>
              )}
            </div>
          </div>
        </div>

        {latestSub?.status === 'Rejected' && latestSub?.rejectionReason && (
          <div style={{
            marginTop: 16, padding: '12px 16px', borderRadius: 10,
            background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
            border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : '#fecaca'}`,
          }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#fca5a5' : '#dc2626', marginBottom: 4 }}>
              ⛔ Rejection Reason
            </p>
            <SafeHtml as="div" html={latestSub.rejectionReason}
              style={{ fontSize: 13, color: isDark ? '#fca5a5' : '#991b1b', lineHeight: 1.55 }} />
          </div>
        )}

        {c.skills?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
            {c.skills.map(s => (
              <span key={s} style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 99,
                background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                color: isDark ? '#a5b4fc' : '#4f46e5',
                border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
              }}>{s}</span>
            ))}
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <div style={{ display: 'flex', gap: 4, padding: '4px', background: isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9', borderRadius: 10, width: 'fit-content' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ ...tabStyle(t.key), flexShrink: 0, whiteSpace: 'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 16 }}>
          <Section title="Personal Details" icon="👤" isDark={isDark}>
            <InfoRow label="Full Name"         value={c.fullName} isDark={isDark} />
            <InfoRow label="Email"             value={c.email} isDark={isDark} />
            <InfoRow label="Phone"             value={c.phone} isDark={isDark} />
            <InfoRow label="Current Location"  value={c.location} isDark={isDark} />
            <InfoRow label="Preferred Location" value={c.preferredLocation} isDark={isDark} />
          </Section>

          <Section title="Professional Details" icon="💼" isDark={isDark}>
            <InfoRow label="Experience"        value={(() => {
              const n = parseFloat(c.experience);
              if (isNaN(n) || n < 0) return null;
              const yrs = Math.floor(n);
              const mos = Math.round((n - yrs) * 12);
              if (yrs > 0 && mos > 0) return `${yrs} yrs ${mos} months`;
              if (yrs > 0) return `${yrs} yrs`;
              if (mos > 0) return `${mos} months`;
              return '0 yrs';
            })()} isDark={isDark} />
            <InfoRow label="Current Company"   value={c.currentCompany} isDark={isDark} />
            <InfoRow label="Current Role"      value={c.currentRole} isDark={isDark} />
            {c.linkedInUrl && (
              <div style={{ padding: '8px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280' }}>LinkedIn</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(c.linkedInUrl).then(() => {
                          const btn = document.getElementById('li-copy-btn');
                          if (btn) { btn.textContent = '✓ Copied'; setTimeout(() => { btn.textContent = 'Copy'; }, 1800); }
                        });
                      }}
                      id="li-copy-btn"
                      style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: isDark ? '#cbd5e1' : '#374151',
                      }}
                    >Copy</button>
                    <a
                      href={c.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                        background: 'linear-gradient(135deg, #0a66c2 0%, #0077b5 100%)',
                        color: '#fff', textDecoration: 'none',
                        boxShadow: '0 1px 4px rgba(10,102,194,0.35)',
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      View Profile
                    </a>
                  </div>
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', wordBreak: 'break-all' }}>
                  {c.linkedInUrl}
                </div>
              </div>
            )}
            {(!isDeveloper || candidate?.createdByRole === 'Developer') && <InfoRow label="Current Salary"  value={fmtLPA(c.currentSalary)}  isDark={isDark} />}
            {(!isDeveloper || candidate?.createdByRole === 'Developer') && <InfoRow label="Expected Salary" value={fmtLPA(c.expectedSalary)} isDark={isDark} />}
            <InfoRow label="Notice Period"     value={c.noticePeriod != null ? (c.noticePeriod === 0 ? 'Immediate' : `${c.noticePeriod} days`) : null} isDark={isDark} />
            {c.noticePeriodNotes && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notice Period Notes</span>
                <SafeHtml as="div" html={c.noticePeriodNotes} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }} />
              </div>
            )}
            <InfoRow label="Availability to Interview" value={c.availabilityToInterview || null} isDark={isDark} />
            {c.experienceSummary && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Relevant Experience Summary</span>
                <SafeHtml as="div" html={c.experienceSummary} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }} />
              </div>
            )}
            <InfoRow label="Offer In Hand"     value={c.offerInHand ? 'Yes' : null} isDark={isDark} />
            {!isDeveloper && latestSub?.overrideBillingCost != null && (
              <InfoRow
                label="Bill Rate (Override)"
                isDark={isDark}
                value={`${latestSub.overrideBillingCurrency || 'USD'} ${latestSub.overrideBillingCost}${latestSub.overrideBillingType ? ` / ${latestSub.overrideBillingType}` : ''}`}
              />
            )}
            {c.relevantExperience && (
              <InfoRow label="Relevant Experience" isDark={isDark} value={(() => {
                const n = parseFloat(c.relevantExperience);
                if (isNaN(n) || n <= 0) return c.relevantExperience; // legacy text fallback
                const yrs = Math.floor(n);
                const mos = Math.round((n - yrs) * 12);
                if (yrs > 0 && mos > 0) return `${yrs} yrs ${mos} months`;
                if (yrs > 0) return `${yrs} yrs`;
                return `${mos} months`;
              })()} />
            )}
            {c.skills?.length > 0 && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 140, flexShrink: 0, paddingTop: 4 }}>Skills</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
                  {c.skills.map(s => (
                    <span key={s} style={{
                      fontSize: 11, padding: '3px 8px', borderRadius: 99,
                      background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                      color: isDark ? '#a5b4fc' : '#4f46e5',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
                    }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {latestSub?.client && (
            <Section title="Client" icon="🏢" isDark={isDark}>
              <InfoRow label="Client Name"  value={latestSub.client.clientName} isDark={isDark} />
              {latestSub.client.industry && <InfoRow label="Industry" value={latestSub.client.industry} isDark={isDark} />}
              {latestSub.client.website  && <InfoRow label="Website"  value={latestSub.client.website}  isDark={isDark} />}
            </Section>
          )}

          {c.checklistSnapshot?.length > 0 && (
            <Section title="Submission Checklist" icon="✅" isDark={isDark}>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                Confirmed at time of submission — locked and cannot be changed.
              </p>
              {c.checklistSnapshot.map((item, i) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7 }}>
                  <span style={{ fontSize: 14, color: isDark ? '#34d399' : '#16a34a', flexShrink: 0, lineHeight: 1.5 }}>✓</span>
                  <SafeHtml as="span" html={item.label} style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 0, flex: 1, lineHeight: 1.5 }} />
                </div>
              ))}
            </Section>
          )}
        </div>
      )}

      {activeTab === 'requirement' && (
        <div className="space-y-4">
          {(c.submissions || []).length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 24px',
              background: isDark ? 'var(--bg-card)' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1'}`,
              borderRadius: 14,
            }}>
              <p style={{ fontSize: 28, marginBottom: 10 }}>📋</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No requirement linked to this candidate.</p>
            </div>
          ) : (
            (c.submissions || []).map((sub, idx) => {
              const req = sub.requirement;
              return (
                <Section
                  key={sub.id}
                  title={req ? req.jobTitle : 'General Submission'}
                  icon="📋"
                  isDark={isDark}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: '0 24px' }}>
                    <InfoRow label="Client"            value={sub.client?.clientName} isDark={isDark} />
                    <InfoRow label="Submission Status" value={sub.status} isDark={isDark} />
                    {req && <>
                      <InfoRow label="Job Type"          value={req.jobType} isDark={isDark} />
                      <InfoRow label="Work Mode"         value={req.workMode} isDark={isDark} />
                      <InfoRow label="Location"          value={req.location} isDark={isDark} />
                      <InfoRow label="Experience Range"  value={req.minExperience != null ? `${req.minExperience}–${req.maxExperience} years` : null} isDark={isDark} />
                      <InfoRow label="Req. Status"       value={req.status} isDark={isDark} />
                      {!isDeveloper && req.budgetMin && (
                        <InfoRow label="Budget" value={`$${Number(req.budgetMin).toLocaleString()} – $${Number(req.budgetMax).toLocaleString()}`} isDark={isDark} />
                      )}
                      {!isDeveloper && (sub.overrideBillingCost || req.billingCost) && (
                        <InfoRow
                          label="Billing"
                          value={sub.overrideBillingCost
                            ? `${sub.overrideBillingCurrency || 'USD'} ${sub.overrideBillingCost} (${sub.overrideBillingType || 'Fixed'}) — override`
                            : `${req.billingCurrency || 'USD'} ${req.billingCost} (${req.billingType || 'Fixed'})`}
                          isDark={isDark}
                        />
                      )}
                    </>}
                  </div>
                </Section>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'interview' && (
        <div className="space-y-4">
          {!hasInterview ? (
            <div style={{
              textAlign: 'center', padding: '40px 24px',
              background: isDark ? 'var(--bg-card)' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1'}`,
              borderRadius: 14,
            }}>
              <p style={{ fontSize: 28, marginBottom: 10 }}>📅</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No interview scheduled for this candidate yet.</p>
            </div>
          ) : (
            interviewSubs.map(sub => (
              <Section
                key={sub.id}
                title={`Interview${sub.requirement ? ` — ${sub.requirement.jobTitle}` : ''} · ${sub.client?.clientName || ''}`}
                icon="📅"
                isDark={isDark}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
                  <InfoRow label="Date" value={sub.interviewDate ? formatDate(sub.interviewDate) : null} isDark={isDark} />
                  <InfoRow label="Time" value={fmtTimeRange(sub.interviewTime, sub.interviewEndTime)} isDark={isDark} />
                  <InfoRow label="Mode" value={sub.interviewMode} isDark={isDark} />
                </div>
                {sub.interviewNotes && (
                  <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, background: isDark ? 'rgba(99,102,241,0.07)' : '#f5f3ff', border: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : '#ddd6fe'}` }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1', marginBottom: 5 }}>Notes</p>
                    <SafeHtml as="div" html={sub.interviewNotes} style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }} />
                  </div>
                )}
                {sub.status === 'Rejected' && (
                  <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2', border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : '#fecaca'}` }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#fca5a5' : '#dc2626', marginBottom: sub.rejectionReason ? 4 : 0 }}>⛔ Rejected</p>
                    {sub.rejectionReason && <SafeHtml as="div" html={sub.rejectionReason} style={{ fontSize: 13, color: isDark ? '#fca5a5' : '#991b1b', lineHeight: 1.55 }} />}
                  </div>
                )}
              </Section>
            ))
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <Section title="Activity History" icon="🕐" isDark={isDark}>
          {logs.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No activity recorded yet.</p>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute', left: 15, top: 0, bottom: 0, width: 2,
                background: isDark ? 'rgba(99,102,241,0.15)' : '#e8ecf1',
              }} />
              <div className="space-y-0">
                {logs.map((log, i) => {
                  let changes = null;
                  try { changes = typeof log.details === 'string' ? JSON.parse(log.details) : log.details; } catch {}

                  return (
                    <div key={log.id} style={{ display: 'flex', gap: 14, paddingBottom: 18, position: 'relative' }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                        border: `2px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, zIndex: 1,
                      }}>
                        {ACTION_ICONS[log.action] || '📝'}
                      </div>
                      <div style={{ flex: 1, paddingTop: 3, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {log.action?.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            by {log.user?.name || 'System'} · {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                        {changes?.status && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>
                            Status → <strong>{changes.status}</strong>
                            {changes.rejectionReason && <span> · Reason: {htmlToText(changes.rejectionReason)}</span>}
                          </p>
                        )}
                        {changes?.changes && Object.keys(changes.changes).length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {Object.entries(changes.changes).slice(0, 5).map(([field, { before, after }]) => (
                              <p key={field} style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, wordBreak: 'break-word' }}>
                                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>{prettyFieldLabel(field)}</span>:
                                {' '}<span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{formatChangeValue(before) || '—'}</span>
                                {' → '}
                                <span style={{ color: isDark ? '#86efac' : '#16a34a' }}>{formatChangeValue(after) || '—'}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* ── Edit Candidate Modal ── */}
      {showEdit && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowEdit(false)}
        >
          <div
            style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', border: '1px solid var(--border-subtle)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1, borderRadius: '16px 16px 0 0' }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>✏️ Edit Candidate</h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{candidate?.fullName}</p>
              </div>
              <button onClick={() => setShowEdit(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* ── Basic Info ── */}
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Basic Info</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="Full Name *" value={editForm.fullName} onChange={e => setEditForm(f => ({ ...f, fullName: e.target.value }))} />
                  <Input label="Email" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  <Input label="Phone" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                  <Input label="Current Company" value={editForm.currentCompany} onChange={e => setEditForm(f => ({ ...f, currentCompany: e.target.value }))} />
                  <Input label="Current Role" value={editForm.currentRole} onChange={e => setEditForm(f => ({ ...f, currentRole: e.target.value }))} />
                  <Input label="Current Location" value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
                  <Input label="Preferred Location" value={editForm.preferredLocation} onChange={e => setEditForm(f => ({ ...f, preferredLocation: e.target.value }))} />
                  <Input label="Availability to Interview" placeholder="e.g. Weekday evenings" value={editForm.availabilityToInterview} onChange={e => setEditForm(f => ({ ...f, availabilityToInterview: e.target.value }))} />
                </div>
                {/* Relevant Experience Summary — full-width rich text */}
                <div style={{ marginTop: 12 }}>
                  <RichTextEditor label="Relevant Experience Summary" rows={4}
                    placeholder="Write-up of the candidate's relevant experience — domains, key projects, achievements…"
                    value={editForm.experienceSummary}
                    onChange={(html) => setEditForm(f => ({ ...f, experienceSummary: html }))} />
                </div>
                {/* Notice Period Notes — full-width rich text */}
                <div style={{ marginTop: 12 }}>
                  <RichTextEditor label="Notice Period Notes" rows={3}
                    placeholder="e.g. Negotiable to 15 days with buyout, serving — LWD flexible…"
                    value={editForm.noticePeriodNotes}
                    onChange={(html) => setEditForm(f => ({ ...f, noticePeriodNotes: html }))} />
                </div>
                {/* Total Experience — full width yrs/mos */}
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>Total Experience</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-input)', padding: '0 10px' }}>
                      <input type="text" inputMode="numeric" placeholder="0" value={editForm.experienceYrs}
                        onChange={e => setEditForm(f => ({ ...f, experienceYrs: onlyDigits(e.target.value) }))}
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>yrs</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-input)', padding: '0 10px' }}>
                      <input type="text" inputMode="numeric" placeholder="0" value={editForm.experienceMos}
                        onChange={e => setEditForm(f => ({ ...f, experienceMos: onlyDigits(e.target.value) }))}
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>mos</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Compensation & Availability ── */}
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Compensation & Availability</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Current Salary with currency */}
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Current Salary (LPA)</label>
                    <div style={{ display: 'flex', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                      <select value={editForm.currentSalaryCurrency} onChange={e => setEditForm(f => ({ ...f, currentSalaryCurrency: e.target.value }))}
                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: 'none', borderRight: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, padding: '0 10px', cursor: 'pointer', outline: 'none', minWidth: 72 }}>
                        {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="text" inputMode="decimal" value={editForm.currentSalary} onChange={e => setEditForm(f => ({ ...f, currentSalary: onlyDecimal(e.target.value) }))} placeholder="e.g. 60000"
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px' }} />
                    </div>
                  </div>

                  {/* Expected Salary with currency */}
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Expected Salary (LPA)</label>
                    <div style={{ display: 'flex', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                      <select value={editForm.expectedSalaryCurrency} onChange={e => setEditForm(f => ({ ...f, expectedSalaryCurrency: e.target.value }))}
                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: 'none', borderRight: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, padding: '0 10px', cursor: 'pointer', outline: 'none', minWidth: 72 }}>
                        {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="text" inputMode="decimal" value={editForm.expectedSalary} onChange={e => setEditForm(f => ({ ...f, expectedSalary: onlyDecimal(e.target.value) }))} placeholder="e.g. 80000"
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px' }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Enter in LPA — a raw amount is auto-read as LPA (e.g. 1500000 = 15 LPA).
                    </span>
                  </div>

                  {/* Notice Period — the Serving flag stays; the numeric days /
                      last-working-date input was retired (details live in the
                      full-width Notice Period Notes editor below, typed once). */}
                  <div className="flex flex-col gap-1.5">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                        Notice Period
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!editForm.servingNoticePeriod}
                          onChange={e => setEditForm(f => ({ ...f, servingNoticePeriod: e.target.checked }))}
                          style={{ width: 13, height: 13, accentColor: '#6366f1' }} />
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Serving</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Offer In Hand ── */}
              <div
                onClick={() => setEditForm(f => ({ ...f, offerInHand: !f.offerInHand, offerCompany: '', offerPackage: '', offerDoj: '' }))}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${editForm.offerInHand ? (isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe') : 'var(--border-subtle)'}`, background: editForm.offerInHand ? (isDark ? 'rgba(99,102,241,0.08)' : '#eef2ff') : 'var(--bg-card)', transition: 'all 0.15s' }}
              >
                <div style={{ width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0, background: editForm.offerInHand ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'), transition: 'background 0.2s' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: editForm.offerInHand ? 18 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: editForm.offerInHand ? (isDark ? '#a5b4fc' : '#4f46e5') : 'var(--text-primary)', lineHeight: 1.3 }}>Offer In Hand</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{editForm.offerInHand ? 'Fill in offer details below' : 'Candidate has an existing offer'}</p>
                </div>
              </div>

              {editForm.offerInHand && (
                <div style={{ background: isDark ? 'rgba(234,179,8,0.06)' : 'rgba(234,179,8,0.06)', border: `1px solid ${isDark ? 'rgba(234,179,8,0.25)' : 'rgba(234,179,8,0.35)'}`, borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#fbbf24' : '#92400e', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>🤝 Offer Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Input label="Company Name" value={editForm.offerCompany} onChange={e => setEditForm(f => ({ ...f, offerCompany: e.target.value }))} placeholder="e.g. Google" />
                    <Input label="Package Offered" value={editForm.offerPackage} onChange={e => setEditForm(f => ({ ...f, offerPackage: e.target.value }))} placeholder="e.g. 18 LPA" />
                    <Input label="Date of Joining" type="date" value={editForm.offerDoj} onChange={e => setEditForm(f => ({ ...f, offerDoj: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* ── Skills & Experience ── */}
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Skills & Experience</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Input label="Skills (comma-separated)" value={editForm.skills} onChange={e => setEditForm(f => ({ ...f, skills: e.target.value }))} placeholder="react, node, postgresql…" />
                  <RichTextEditor label="Internal Notes" value={editForm.notes} rows={4}
                    onChange={(html) => setEditForm(f => ({ ...f, notes: html }))} />
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button onClick={() => setShowEdit(false)} style={{ fontSize: 13, padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  style={{ fontSize: 13, fontWeight: 600, padding: '9px 24px', borderRadius: 8, border: 'none', background: saving ? 'var(--bg-raised)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: saving ? 'var(--text-muted)' : '#fff', cursor: saving ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
                  {saving ? 'Saving…' : '✓ Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Resubmit Modal ── */}
      {showResubmit && (() => {
        const re = resubmitEditForm;
        const setRe = (key, val) => setResubmitEditForm(prev => ({ ...prev, [key]: val }));
        const canResubmit = !!resubmitReqId && !resubmitting && !!candidate.resumeName;
        const alreadySubmittedIds = new Set(
          (c.submissions || []).map(s => s.requirementId).filter(Boolean)
        );
        const availableReqs = allReqs.filter(
          r => r.status !== 'Closed' && !alreadySubmittedIds.has(r.id)
        );
        const STATUS_COLORS = {
          Applied:     { bg: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',   color: isDark ? '#a5b4fc' : '#4f46e5'  },
          Shortlisted: { bg: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb',   color: isDark ? '#fcd34d' : '#b45309'  },
          Interview:   { bg: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff',   color: isDark ? '#93c5fd' : '#1d4ed8'  },
          Hired:       { bg: isDark ? 'rgba(16,185,129,0.15)' : '#f0fdf4',   color: isDark ? '#6ee7b7' : '#065f46'  },
          Rejected:    { bg: isDark ? 'rgba(239,68,68,0.15)'  : '#fff5f5',   color: isDark ? '#fca5a5' : '#b91c1c'  },
        };
        const inp = { width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13, background: isDark ? 'rgba(255,255,255,0.06)' : '#f7f8fa', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' };
        const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: isDark ? 'var(--text-muted)' : '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };
        const card = { background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.09)' : '#e2e6eb'}`, borderRadius: 12, boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' };
        const cardHead = (emoji, title) => (
          <div style={{ padding: '11px 16px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f4f6f9', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e6eb'}`, borderRadius: '11px 11px 0 0', display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 15 }}>{emoji}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? 'var(--text-secondary)' : '#374151', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
          </div>
        );
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowResubmit(false)}
          >
            <div
              style={{ background: isDark ? 'var(--bg-card)' : '#f4f6f9', borderRadius: 18, width: '100%', maxWidth: 780, maxHeight: '93vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.4)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0e4ea'}`, overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              {/* ── Header ── */}
              <div style={{ padding: '18px 24px', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e0e4ea'}`, display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.02)' : '#ffffff', borderRadius: '18px 18px 0 0' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#0d9488,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🔁</div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Resubmit Candidate</h2>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Update <strong style={{ color: 'var(--text-secondary)' }}>{c.fullName}</strong>'s details then pick a new requirement</p>
                </div>
                <button onClick={() => setShowResubmit(false)} style={{ width: 32, height: 32, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
              </div>

              {/* ── Scrollable Body ── */}
              <div style={{ overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>

                {/* Basic Info card */}
                <div style={card}>
                  {cardHead('👤', 'Basic Information')}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5" style={{ padding: '16px' }}>
                    <div><label style={lbl}>Full Name *</label><input style={inp} value={re.fullName || ''} onChange={e => setRe('fullName', e.target.value)} placeholder="Full name" /></div>
                    <div>
                      <label style={lbl}>Email</label>
                      <input style={{ ...inp, ...(re.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(re.email) ? { borderColor: '#ef4444' } : {}) }} type="email" value={re.email || ''} onChange={e => setRe('email', e.target.value)} placeholder="Email address" />
                      {re.email && !re.email.includes('@') && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Email must include @</p>}
                      {re.email && re.email.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(re.email) && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Enter a valid email (e.g. user@domain.com)</p>}
                    </div>
                    <div><label style={lbl}>Phone</label><input style={inp} value={re.phone || ''} onChange={e => setRe('phone', e.target.value)} placeholder="Phone number" /></div>
                    <div><label style={lbl}>Current Company</label><input style={inp} value={re.currentCompany || ''} onChange={e => setRe('currentCompany', e.target.value)} placeholder="Company name" /></div>
                    <div><label style={lbl}>Current Role</label><input style={inp} value={re.currentRole || ''} onChange={e => setRe('currentRole', e.target.value)} placeholder="Job title" /></div>
                    <div><label style={lbl}>Current Location</label><input style={inp} value={re.location || ''} onChange={e => setRe('location', e.target.value)} placeholder="City, Country" /></div>
                    <div><label style={lbl}>Preferred Location</label><input style={inp} value={re.preferredLocation || ''} onChange={e => setRe('preferredLocation', e.target.value)} placeholder="Preferred city" /></div>
                  </div>
                  {/* Total Experience full-width yrs/mos */}
                  <div style={{ padding: '0 16px 16px' }}>
                    <label style={lbl}>Total Experience</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.06)' : '#f7f8fa', padding: '0 10px' }}>
                        <input type="text" inputMode="numeric" placeholder="0" value={re.experienceYrs ?? ''}
                          onChange={e => setRe('experienceYrs', onlyDigits(e.target.value))}
                          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>yrs</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.06)' : '#f7f8fa', padding: '0 10px' }}>
                        <input type="text" inputMode="numeric" placeholder="0" value={re.experienceMos ?? ''}
                          onChange={e => setRe('experienceMos', onlyDigits(e.target.value))}
                          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>mos</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compensation card */}
                <div style={card}>
                  {cardHead('💰', 'Compensation & Availability')}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5" style={{ padding: '16px' }}>
                    <div>
                      <label style={lbl}>Current Salary (LPA)</label>
                      <div style={{ display: 'flex', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, borderRadius: 8, overflow: 'hidden' }}>
                        <select value={re.currentSalaryCurrency || 'INR'} onChange={e => setRe('currentSalaryCurrency', e.target.value)}
                          style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#eef0f4', border: 'none', borderRight: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, padding: '9px 10px', fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
                          {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(cur => <option key={cur}>{cur}</option>)}
                        </select>
                        <input type="text" inputMode="decimal" value={re.currentSalary || ''} onChange={e => setRe('currentSalary', onlyDecimal(e.target.value))} placeholder="Amount"
                          style={{ flex: 1, padding: '9px 12px', fontSize: 13, background: isDark ? 'rgba(255,255,255,0.06)' : '#f7f8fa', border: 'none', color: 'var(--text-primary)', outline: 'none', minWidth: 0 }} />
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Expected Salary (LPA)</label>
                      <div style={{ display: 'flex', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, borderRadius: 8, overflow: 'hidden' }}>
                        <select value={re.expectedSalaryCurrency || 'INR'} onChange={e => setRe('expectedSalaryCurrency', e.target.value)}
                          style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#eef0f4', border: 'none', borderRight: `1.5px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, padding: '9px 10px', fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
                          {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(cur => <option key={cur}>{cur}</option>)}
                        </select>
                        <input type="text" inputMode="decimal" value={re.expectedSalary || ''} onChange={e => setRe('expectedSalary', onlyDecimal(e.target.value))} placeholder="Amount"
                          style={{ flex: 1, padding: '9px 12px', fontSize: 13, background: isDark ? 'rgba(255,255,255,0.06)' : '#f7f8fa', border: 'none', color: 'var(--text-primary)', outline: 'none', minWidth: 0 }} />
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Notice Period</label>
                      <div style={{ display: 'flex', background: isDark ? 'rgba(255,255,255,0.07)' : '#eef0f4', borderRadius: 99, padding: 3, gap: 2, marginBottom: 10, width: 'fit-content' }}>
                        <button type="button" onClick={() => setRe('servingNoticePeriod', false)}
                          style={{ fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 99, border: 'none', cursor: 'pointer', transition: 'all 0.15s', background: !re.servingNoticePeriod ? '#6366f1' : 'transparent', color: !re.servingNoticePeriod ? '#fff' : 'var(--text-muted)' }}>
                          Fixed Days
                        </button>
                        <button type="button" onClick={() => setRe('servingNoticePeriod', true)}
                          style={{ fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 99, border: 'none', cursor: 'pointer', transition: 'all 0.15s', background: re.servingNoticePeriod ? '#f59e0b' : 'transparent', color: re.servingNoticePeriod ? '#fff' : 'var(--text-muted)' }}>
                          🕐 Serving
                        </button>
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                        Details go in Notice Period Notes below.
                      </p>
                    </div>
                    <div>
                      <label style={lbl}>Availability to Interview</label>
                      <input style={inp} value={re.availabilityToInterview || ''} onChange={e => setRe('availabilityToInterview', e.target.value)} placeholder="e.g. Weekday evenings" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <RichTextEditor label="Relevant Experience Summary" rows={3}
                        placeholder="Write-up of the candidate's relevant experience…"
                        value={re.experienceSummary || ''}
                        onChange={(html) => setRe('experienceSummary', html)} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <RichTextEditor label="Notice Period Notes" rows={3}
                        placeholder="e.g. Negotiable to 15 days with buyout…"
                        value={re.noticePeriodNotes || ''}
                        onChange={(html) => setRe('noticePeriodNotes', html)} />
                    </div>
                  </div>
                </div>

                {/* Offer In Hand card */}
                <div style={card}>
                  {cardHead('🤝', 'Offer In Hand')}
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : '#f7f8fa', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e6eb'}`, flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#111827', margin: 0 }}>Candidate has a competing offer?</p>
                        <p style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#6b7280', marginTop: 3, marginBottom: 0 }}>Enable to log company, package &amp; joining date</p>
                      </div>
                      <div onClick={() => setRe('offerInHand', !re.offerInHand)}
                        style={{ width: 48, height: 27, borderRadius: 99, background: re.offerInHand ? '#10b981' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'), cursor: 'pointer', position: 'relative', transition: 'background 0.25s', flexShrink: 0, marginLeft: 16 }}>
                        <div style={{ position: 'absolute', top: 3, left: re.offerInHand ? 24 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left 0.25s', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                    {re.offerInHand && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" style={{ marginTop: 12 }}>
                        <div><label style={lbl}>Company</label><input style={inp} value={re.offerCompany || ''} onChange={e => setRe('offerCompany', e.target.value)} placeholder="Company name" /></div>
                        <div><label style={lbl}>Package</label><input style={inp} value={re.offerPackage || ''} onChange={e => setRe('offerPackage', e.target.value)} placeholder="e.g. 15 LPA" /></div>
                        <div><label style={lbl}>Date of Joining</label><input type="date" style={inp} value={re.offerDoj || ''} onChange={e => setRe('offerDoj', e.target.value)} /></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Skills card */}
                <div style={card}>
                  {cardHead('🛠', 'Skills & Experience')}
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div><label style={lbl}>Skills (comma-separated)</label><input style={inp} value={re.skills || ''} onChange={e => setRe('skills', e.target.value)} placeholder="e.g. React, Node.js, Python" /></div>
                    <div><RichTextEditor label="Notes" rows={3} value={re.notes || ''} onChange={(html) => setRe('notes', html)} placeholder="Internal recruiter notes…" /></div>
                  </div>
                </div>

                {/* ── Submit Zone ── */}
                <div style={{ background: isDark ? 'rgba(13,148,136,0.07)' : '#f0fdfa', border: `1px solid ${isDark ? 'rgba(13,148,136,0.2)' : '#99f6e4'}`, borderRadius: 12 }}>
                  <div style={{ padding: '11px 16px', background: isDark ? 'rgba(13,148,136,0.1)' : '#ccfbf1', borderBottom: `1px solid ${isDark ? 'rgba(13,148,136,0.15)' : '#99f6e4'}`, borderRadius: '11px 11px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15 }}>📋</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#5eead4' : '#0f766e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Submit to Requirement</span>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {c.submissions?.length > 0 && (
                      <div>
                        <p style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Previously submitted to</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {c.submissions.map(s => {
                            const sc = STATUS_COLORS[s.status] || STATUS_COLORS.Applied;
                            return (
                              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 8px', borderRadius: 99, background: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, fontSize: 11, maxWidth: '100%', wordBreak: 'break-word', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 500, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{s.requirement?.jobTitle || '—'}</span>
                                {s.client?.clientName && <span style={{ color: 'var(--text-muted)' }}>· {s.client.clientName}</span>}
                                <span style={{ padding: '1px 7px', borderRadius: 99, fontWeight: 600, fontSize: 10, background: sc.bg, color: sc.color }}>{s.status}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Select new requirement *</label>
                      {availableReqs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '16px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`, color: 'var(--text-muted)', fontSize: 13 }}>
                          🎉 Already submitted to all available open requirements.
                        </div>
                      ) : (
                        <SearchableSelect
                          required
                          placeholder="— Pick a requirement —"
                          value={resubmitReqId}
                          onChange={val => setResubmitReqId(val)}
                          options={availableReqs.map(r => ({
                            value: r.id,
                            label: r.jobTitle,
                            sub:   `${r.client?.clientName || ''}${r.minExperience != null ? ` · ${r.minExperience}–${r.maxExperience || '?'} yrs` : ''} · ${r.status}`,
                          }))}
                        />
                      )}
                    </div>
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Submission Notes (optional)</label>
                      <RichTextEditor rows={3} value={resubmitNotes} onChange={(html) => setResubmitNotes(html)} placeholder="Any specific notes for this submission…" />
                    </div>
                  </div>
                </div>

              </div>

              {/* ── Footer ── */}
              <div style={{ padding: '14px 20px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e0e4ea'}`, display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.02)' : '#ffffff', borderRadius: '0 0 18px 18px' }}>
                {!candidate.resumeName && (
                  <span style={{ marginRight: 'auto', fontSize: 12, color: isDark ? '#f87171' : '#dc2626' }}>
                    ⚠ No resume on file — upload it via Candidates → Edit before resubmitting.
                  </span>
                )}
                <button onClick={() => setShowResubmit(false)}
                  style={{ fontSize: 13, padding: '9px 20px', borderRadius: 9, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 500 }}>
                  Cancel
                </button>
                <button onClick={handleResubmit}
                  disabled={!resubmitReqId || resubmitting || availableReqs.length === 0 || !candidate.resumeName}
                  style={{
                    fontSize: 13, fontWeight: 600, padding: '9px 24px', borderRadius: 9, display: 'flex', alignItems: 'center', gap: 7,
                    cursor: canResubmit ? 'pointer' : 'not-allowed',
                    background: canResubmit ? 'linear-gradient(135deg,#0d9488,#0891b2)' : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                    color: canResubmit ? '#fff' : 'var(--text-muted)',
                    border: 'none', transition: 'all 0.15s',
                    boxShadow: canResubmit ? '0 4px 14px rgba(13,148,136,0.35)' : 'none',
                  }}>
                  {resubmitting ? (
                    <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Saving…</>
                  ) : (
                    <><span>🔁</span> Save &amp; Submit</>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}